"use client"

import { motion } from "framer-motion"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowLeft, Shield, Eye, Lock, FileText } from "lucide-react"
import { Link } from "react-router-dom"
import Footer from "@/components/Common/Footer"
import ERPLeadBot from "./ERPLeadBot";


export default function PrivacyPolicy() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="max-w-4xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header Section */}
          <motion.div className="text-center mb-12" variants={fadeIn}>
            <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
              <Shield className="h-4 w-4 mr-2" />
              PRIVACY POLICY
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600">
              How we collect, use, and protect your information
            </p>
          </motion.div>

          {/* Policy Content */}
          <motion.div variants={fadeIn}>
            <Card className="shadow-lg border-0">
              <CardContent className="p-8">
                <div className="prose prose-lg max-w-none">
                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Eye className="h-6 w-6 mr-2 text-blue-600" />
                      Introduction
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      This Privacy Policy describes how Clynox Solutions and its affiliates (collectively "Clynox Solutions, we, our, us") collect, use, share, protect or otherwise process your information/ personal data through our website www.clynox.com (hereinafter referred to as Platform). Please note that you may be able to browse certain sections of the Platform without registering with us. We do not offer any product/service under this Platform outside India and your personal data will primarily be stored and processed in India.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      By visiting this Platform, providing your information or availing any product/service offered on the Platform, you expressly agree to be bound by the terms and conditions of this Privacy Policy, the Terms of Use and the applicable service/product terms and conditions, and agree to be governed by the laws of India including but not limited to the laws applicable to data protection and privacy. If you do not agree please do not use or access our Platform.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-6 w-6 mr-2 text-blue-600" />
                      Collection
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We collect your personal data when you use our Platform, services or otherwise interact with us during the course of our relationship and related information provided from time to time. Some of the information that we may collect includes but is not limited to personal data / information provided to us during sign-up/registering or using our Platform such as name, date of birth, address, telephone/mobile number, email ID and/or any such information shared as proof of identity or address.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      Some of the sensitive personal data may be collected with your consent, such as your bank account or credit or debit card or other payment instrument information or biometric information such as your facial features or physiological information (in order to enable use of certain features when opted for, available on the Platform) etc all of the above being in accordance with applicable law(s).
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      You always have the option to not provide information, by choosing not to use a particular service or feature on the Platform. We may track your behaviour, preferences, and other information that you choose to provide on our Platform. This information is compiled and analysed on an aggregated basis.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Lock className="h-6 w-6 mr-2 text-blue-600" />
                      Usage
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We use personal data to provide the services you request. To the extent we use your personal data to market to you, we will provide you the ability to opt-out of such uses. We use your personal data to assist sellers and business partners in handling and fulfilling orders; enhancing customer experience; to resolve disputes; troubleshoot problems; inform you about online and offline offers, products, services, and updates; customise your experience; detect and protect us against error, fraud and other criminal activity; enforce our terms and conditions; conduct marketing research, analysis and surveys; and as otherwise described to you at the time of collection of information.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      You understand that your access to these products/services may be affected in the event permission is not provided to us.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Sharing</h2>
                    <p className="text-gray-700 leading-relaxed">
                      We may share your personal data internally within our group entities, our other corporate entities, and affiliates to provide you access to the services and products offered by them. These entities and affiliates may market to you as a result of such sharing unless you explicitly opt-out.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      We may disclose personal data to third parties such as sellers, business partners, third party service providers including logistics partners, prepaid payment instrument issuers, third-party reward programs and other payment opted by you. These disclosure may be required for us to provide you access to our services and products offered to you, to comply with our legal obligations, to enforce our user agreement, to facilitate our marketing and advertising activities, to prevent, detect, mitigate, and investigate fraudulent or illegal activities related to our services.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Precautions</h2>
                    <p className="text-gray-700 leading-relaxed">
                      To protect your personal data from unauthorised access or disclosure, loss or misuse we adopt reasonable security practices and procedures. Once your information is in our possession or whenever you access your account information, we adhere to our security guidelines to protect it against unauthorised access and offer the use of a secure server.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      However, the transmission of information is not completely secure for reasons beyond our control. By using the Platform, the users accept the security implications of data transmission over the internet and the World Wide Web which cannot always be guaranteed as completely secure, and therefore, there would always remain certain inherent risks regarding use of the Platform. Users are responsible for ensuring the protection of login and password records for their account.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Deletion and Retention</h2>
                    <p className="text-gray-700 leading-relaxed">
                      You have an option to delete your account by visiting your profile and settings on our Platform, this action would result in you losing all information related to your account. You may also write to us at the contact information provided below to assist you with these requests.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      We may in event of any pending grievance, claims, pending shipments or any other services we may refuse or delay deletion of the account. Once the account is deleted, you will lose access to the account. We retain your personal data information for a period no longer than is required for the purpose for which it was collected or as required under any applicable law.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
                    <p className="text-gray-700 leading-relaxed">
                      You may access, rectify, and update your personal data directly through the functionalities provided on the Platform.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Consent</h2>
                    <p className="text-gray-700 leading-relaxed">
                      By visiting our Platform or by providing your information, you consent to the collection, use, storage, disclosure and otherwise processing of your information on the Platform in accordance with this Privacy Policy. If you disclose to us any personal data relating to other people, you represent that you have the authority to do so and permit us to use the information in accordance with this Privacy Policy.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      You, while providing your personal data over the Platform or any partner platforms or establishments, consent to us (including our other corporate entities, affiliates, lending partners, technology partners, marketing channels, business partners and other third parties) to contact you through SMS, instant messaging apps, call and/or e-mail for the purposes specified in this Privacy Policy.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to this Privacy Policy</h2>
                    <p className="text-gray-700 leading-relaxed">
                      Please check our Privacy Policy periodically for changes. We may update this Privacy Policy to reflect changes to our information practices. We may alert / notify you about the significant changes to the Privacy Policy, in the manner as may be required under applicable laws.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Grievance Officer</h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <p className="text-gray-700 mb-2"><strong>Name:</strong> Abheesh Bhardwaj</p>
                      <p className="text-gray-700 mb-2"><strong>Phone:</strong> +91-7499070944</p>
                      <p className="text-gray-700"><strong>Time:</strong> Monday - Friday (9:00 - 18:00)</p>
                    </div>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
      <ERPLeadBot />
    </div>
  )
} 